
import { karmaService } from './karmaService';
import { feedService } from './feedService';
import { supabaseClient } from './supabaseClient';
import { v4 as uuidv4 } from 'uuid';
import { createClient, SupabaseClient } from '@supabase/supabase-js';

export interface GameStateBranch {
  id: string;
  genre: string; // e.g., 'cyberpunk', 'fantasy', 'horror'
  cartridgeId: string;
  cartridgeName: string;
  instanceId: string; // Unique ID for this game instance
  branchData: {
    name: string; // Branch name (e.g., 'main', 'player-123-choices')
    head: string; // Current commit ID
    mergedFrom?: string[]; // Parent branches that were merged
    createdBy: string; // Player ID who created this branch
    isMainBranch: boolean;
  };
  playerChoices: PlayerChoiceCommit[];
  karmaState: number; // Current collective karma of this branch
  createdAt: Date;
  updatedAt: Date;
  mergeableWith: string[]; // Branch IDs that can merge with this one
}

export interface PlayerChoiceCommit {
  id: string;
  hash: string; // Commit hash
  branchId: string;
  playerId: string;
  playerName: string;
  parentCommitId?: string;
  parentCommit?: string; // For compatibility
  choiceData: {
    segmentId: string;
    choiceText: string;
    consequences: string;
    karmaImpact: number;
    emptinessShift: number;
    storyDelta: any; // Changes to story state
  };
  choice?: { // For compatibility
    segmentId: string;
    choiceText: string;
    consequences: string;
    karmaImpact: number;
  };
  commitMessage: string;
  karmaDelta: number;
  timestamp: Date | string;
  worldStateChanges?: string[];
}

export interface BranchMergeRequest {
  id: string;
  sourceBranchId: string;
  targetBranchId: string;
  requesterId: string;
  status: 'pending' | 'approved' | 'rejected' | 'merged' | 'conflicted';
  conflictData?: {
    conflictingChoices: PlayerChoiceCommit[];
    karmaDiscrepancy: number;
  };
  resolution?: {
    method: 'karma_weighted' | 'community_vote' | 'faction_leader';
    result: any;
  };
  createdAt: Date;
  resolvedAt?: Date;
}

export interface GenreShift {
  fromGenre: string;
  toGenre: string;
  triggerKarma: number;
  requiredConsensus: number; // Percentage of players needed
  description: string;
}

export interface KarmaFlowData {
  timestamp: string;
  totalKarma: number;
  playerCount: number;
  dominantAlignment: string;
}

export interface CartridgeStats {
  currentGenre: string;
  playerCount: number;
  averageKarma: number;
  dominantChoicePattern: string;
  worldEventsTriggered: number;
}

export interface BranchStats {
  totalCommits: number;
  activePlayers: number;
  lastActivity: string;
}

export interface MergeableOption {
  branchId: string;
  branchData: GameStateBranch['branchData'];
  karmaDifference: number;
  sharedCommits: number;
}

export interface MergeResult {
  status: 'success' | 'conflict';
  mergedBranch?: GameStateBranch;
  conflictsResolved?: number;
  conflictReason?: string;
}

export interface GenreMutation {
  oldGenre: string;
  newGenre: string;
  affectedBranches: Array<{ playerId: string }>;
}

class GitGameStateService {
  /**
   * Get Supabase client
   */
  private getSupabase() {
    return supabaseClient;
  }
  private genreShiftThresholds: GenreShift[] = [
    {
      fromGenre: 'cyberpunk',
      toGenre: 'biopunk',
      triggerKarma: -100,
      requiredConsensus: 0.6,
      description: 'Excessive body modifications corrupt the digital realm into biological horror'
    },
    {
      fromGenre: 'cyberpunk',
      toGenre: 'solarpunk',
      triggerKarma: 100,
      requiredConsensus: 0.7,
      description: 'Collective enlightenment transforms the dystopia into sustainable utopia'
    },
    {
      fromGenre: 'fantasy',
      toGenre: 'grimdark',
      triggerKarma: -150,
      requiredConsensus: 0.5,
      description: 'Dark choices plunge the realm into eternal shadow'
    }
  ];

  /**
   * Initialize a new game branch
   */
  async createGameBranch(
    genre: string,
    cartridgeId: string,
    cartridgeName: string,
    instanceId: string,
    createdBy: string,
    isMainBranch: boolean = false
  ): Promise<GameStateBranch> {
    const branchId = uuidv4();
    const initialCommitId = uuidv4();

    const branch: GameStateBranch = {
      id: branchId,
      genre,
      cartridgeId,
      cartridgeName,
      instanceId,
      branchData: {
        name: isMainBranch ? 'main' : `player-${createdBy}-branch`,
        head: initialCommitId,
        createdBy,
        isMainBranch
      },
      playerChoices: [{
        id: initialCommitId,
        hash: initialCommitId,
        branchId,
        playerId: createdBy,
        playerName: 'System',
        choiceData: {
          segmentId: 'initial',
          choiceText: 'Begin the dream',
          consequences: 'A new story branch emerges from the void',
          karmaImpact: 0,
          emptinessShift: 0,
          storyDelta: { initialized: true }
        },
        commitMessage: 'Initial commit - dream inception',
        karmaDelta: 0,
        timestamp: new Date()
      }],
      karmaState: 0,
      createdAt: new Date(),
      updatedAt: new Date(),
      mergeableWith: []
    };

    const { error } = await this.getSupabase()
      .from('game_state_branches')
      .insert({
        id: branchId,
        genre,
        cartridge_id: cartridgeId,
        instance_id: instanceId,
        branch_data: {
          name: branch.branchData.name,
          head: initialCommitId,
          createdBy,
          isMainBranch,
          cartridgeName
        },
        player_choices: [], // Empty JSONB array
        karma_state: 0
      });

    if (error) throw error;

    return branch;
  }

  /**
   * Create a new branch (simplified API)
   */
  async createBranch(
    genre: string,
    cartridgeId: string,
    playerId: string,
    playerName: string
  ): Promise<GameStateBranch> {
    const instanceId = `instance_${Date.now()}`;
    return this.createGameBranch(
      genre,
      cartridgeId,
      'Story Cartridge',
      instanceId,
      playerId,
      false
    );
  }

  /**
   * Record a player's choice as a commit
   */
  async commitPlayerChoice(
    branchId: string,
    playerId: string,
    playerName: string,
    choice: {
      segmentId: string;
      choiceText: string;
      consequences: string;
      karmaImpact: number;
    }
  ): Promise<PlayerChoiceCommit> {
    const { data: branch, error: fetchError } = await this.getSupabase()
      .from('game_state_branches')
      .select('*')
      .eq('id', branchId)
      .single();

    if (fetchError) throw fetchError;

    const commitId = uuidv4();
    const commit: PlayerChoiceCommit = {
      id: commitId,
      hash: commitId,
      branchId,
      playerId,
      playerName,
      parentCommitId: branch.branch_data?.head || 'initial',
      choiceData: {
        segmentId: choice.segmentId,
        choiceText: choice.choiceText,
        consequences: choice.consequences,
        karmaImpact: choice.karmaImpact,
        emptinessShift: 0,
        storyDelta: {}
      },
      choice, // For compatibility
      commitMessage: `${playerName}: ${choice.choiceText}`,
      karmaDelta: choice.karmaImpact,
      timestamp: new Date().toISOString(),
      worldStateChanges: []
    };

    // Update branch with correct schema
    const newKarma = branch.karma_state + choice.karmaImpact;
    const existingChoices = Array.isArray(branch.player_choices) ? branch.player_choices : [];
    
    const { error: updateError } = await this.getSupabase()
      .from('game_state_branches')
      .update({
        branch_data: {
          ...branch.branch_data,
          head: commitId
        },
        karma_state: newKarma,
        updated_at: new Date().toISOString()
      })
      .eq('id', branchId);

    // Also insert into player_choice_commits table
    const { error: commitError } = await this.getSupabase()
      .from('player_choice_commits')
      .insert({
        id: commitId,
        branch_id: branchId,
        player_id: playerId,
        choice_data: commit.choiceData,
        commit_message: commit.commitMessage,
        karma_delta: choice.karmaImpact
      });

    if (updateError) throw updateError;
    if (commitError) throw commitError;

    // Record karma action
    await karmaService.recordKarmaAction(
      playerId,
      'story',
      choice.karmaImpact,
      choice.consequences
    );

    // Update karma flow
    const cartridgeStats = await this.getCartridgeStats(branch.cartridge_id);
    await this.getSupabase().from('karma_flow').insert({
        cartridge_id: branch.cartridge_id,
        total_karma: cartridgeStats.averageKarma * cartridgeStats.playerCount,
        player_count: cartridgeStats.playerCount,
        dominant_alignment: cartridgeStats.dominantChoicePattern
    });

    // Check for world state changes
    if (Math.abs(choice.karmaImpact) >= 20) {
      commit.worldStateChanges = [`Major karma shift: ${choice.karmaImpact > 0 ? 'Light' : 'Dark'} energy ripples through the world`];
      
      // Create feed post for significant choices
      await feedService.createWorldEventPost({
        title: 'Reality Trembles',
        description: `${playerName}'s choice sends ripples through the dream: "${choice.choiceText}"`,
        affectedPlayers: [playerId],
        karmaRequirement: 0
      });
    }

    return commit;
  }

  /**
   * Get all branches for a cartridge
   */
  async getCartridgeBranches(cartridgeId: string): Promise<GameStateBranch[]> {
    const { data, error } = await this.getSupabase()
      .from('game_state_branches')
      .select('*')
      .eq('cartridge_id', cartridgeId);

    if (error) throw error;

    return (data || []).map(this.transformBranchData);
  }

  /**
   * Get all branches (admin)
   */
  async getAllBranches(): Promise<GameStateBranch[]> {
    const { data, error } = await this.getSupabase()
      .from('game_state_branches')
      .select('*');

    if (error) throw error;

    return (data || []).map(this.transformBranchData);
  }

  /**
   * Get a specific branch
   */
  async getBranch(branchId: string): Promise<GameStateBranch> {
    const { data, error } = await this.getSupabase()
      .from('game_state_branches')
      .select('*')
      .eq('id', branchId)
      .single();

    if (error) throw error;

    return this.transformBranchData(data);
  }

  /**
   * Get branch statistics
   */
  async getBranchStats(branchId: string): Promise<BranchStats> {
    const branch = await this.getBranch(branchId);
    const playerChoices = branch.playerChoices || [];
    
    const uniquePlayers = new Set(playerChoices.map(c => c.playerId));
    const lastChoice = playerChoices[playerChoices.length - 1];

    return {
      totalCommits: playerChoices.length,
      activePlayers: uniquePlayers.size,
      lastActivity: lastChoice?.timestamp?.toString() || branch.createdAt.toString()
    };
  }

  /**
   * Get cartridge statistics
   */
  async getCartridgeStats(cartridgeId: string): Promise<CartridgeStats> {
    const branches = await this.getCartridgeBranches(cartridgeId);
    
    const totalKarma = branches.reduce((sum, b) => sum + b.karmaState, 0);
    const playerCount = new Set(branches.map(b => b.branchData.createdBy)).size;
    const averageKarma = playerCount > 0 ? Math.round(totalKarma / playerCount) : 0;

    // Analyze choice patterns
    const allChoices = branches.flatMap(b => b.playerChoices || []);
    const choiceTypes = allChoices.reduce((acc, choice) => {
      const type = choice.choice?.karmaImpact || choice.choiceData?.karmaImpact || 0;
      const category = type > 0 ? 'positive' : type < 0 ? 'negative' : 'neutral';
      acc[category] = (acc[category] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const dominantPattern = Object.entries(choiceTypes)
      .sort(([, a], [, b]) => b - a)[0]?.[0] || 'neutral';

    return {
      currentGenre: branches[0]?.genre || 'unknown',
      playerCount,
      averageKarma,
      dominantChoicePattern: dominantPattern,
      worldEventsTriggered: 0 // TODO: Track this properly
    };
  }

  /**
   * Get karma flow data for a cartridge
   */
  async getCartridgeKarmaFlow(cartridgeId: string): Promise<KarmaFlowData[]> {
        const { data, error } = await this.getSupabase()
            .from('karma_flow')
            .select('*')
            .eq('cartridge_id', cartridgeId)
            .order('timestamp', { ascending: true });

        if (error) throw error;

        return data;
    }

  /**
   * Find branches that can merge
   */
  async findMergeableBranches(branchId: string, maxKarmaDiff: number = 20): Promise<MergeableOption[]> {
    const sourceBranch = await this.getBranch(branchId);
    const allBranches = await this.getCartridgeBranches(sourceBranch.cartridgeId);
    
    const mergeableOptions: MergeableOption[] = [];

    for (const targetBranch of allBranches) {
      if (targetBranch.id === branchId) continue;
      
      const karmaDiff = Math.abs(sourceBranch.karmaState - targetBranch.karmaState);
      if (karmaDiff <= maxKarmaDiff && sourceBranch.genre === targetBranch.genre) {
        // Count shared commits (simplified)
        const sharedCommits = 1; // TODO: Implement proper shared history detection
        
        mergeableOptions.push({
          branchId: targetBranch.id,
          branchData: targetBranch.branchData,
          karmaDifference: karmaDiff,
          sharedCommits
        });
      }
    }

    return mergeableOptions;
  }

  /**
   * Merge two branches
   */
  async mergeBranches(
    branch1Id: string,
    branch2Id: string,
    initiatorId: string,
    mergeMessage: string
  ): Promise<MergeResult> {
    const branch1 = await this.getBranch(branch1Id);
    const branch2 = await this.getBranch(branch2Id);

    const karmaDiff = Math.abs(branch1.karmaState - branch2.karmaState);

    if (karmaDiff > 20) {
      return {
        status: 'conflict',
        conflictReason: 'karma_divergence'
      };
    }

    if (branch1.genre !== branch2.genre) {
      return {
        status: 'conflict',
        conflictReason: 'genre_mismatch'
      };
    }

    // Create merged branch
    const mergedBranch = await this.createGameBranch(
      branch1.genre,
      branch1.cartridgeId,
      branch1.cartridgeName,
      `merged_${Date.now()}`,
      initiatorId,
      false
    );

    // Combine player choices
    const allChoices = [
      ...branch1.playerChoices,
      ...branch2.playerChoices
    ].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

    // Update merged branch with combined history
    await this.getSupabase()
      .from('game_state_branches')
      .update({
        karma_state: Math.round((branch1.karmaState + branch2.karmaState) / 2),
        metadata: {
          ...mergedBranch,
          playerChoices: allChoices,
          mergedFrom: [branch1Id, branch2Id]
        }
      })
      .eq('id', mergedBranch.id);

    // Create merge commit
    await this.commitPlayerChoice(
      mergedBranch.id,
      initiatorId,
      'System',
      {
        segmentId: 'merge',
        choiceText: mergeMessage,
        consequences: `Merged branches: ${branch1.branchData.name} + ${branch2.branchData.name}`,
        karmaImpact: 0
      }
    );

    return {
      status: 'success',
      mergedBranch,
      conflictsResolved: 0
    };
  }

  /**
   * Force genre mutation
   */
  async mutateGenre(
    cartridgeId: string,
    fromGenre: string,
    toGenre: string,
    reason: string
  ): Promise<GenreMutation> {
    const branches = await this.getCartridgeBranches(cartridgeId);
    
    // Update all branches
    for (const branch of branches) {
      await this.getSupabase()
        .from('game_state_branches')
        .update({ genre: toGenre })
        .eq('id', branch.id);
    }

    // Create feed announcement
    await feedService.createWorldEventPost({
      title: 'Reality Shift!',
      description: `The world transforms from ${fromGenre} to ${toGenre}: ${reason}`,
      affectedPlayers: branches.map(b => b.branchData.createdBy),
      karmaRequirement: 0
    });

    return {
      oldGenre: fromGenre,
      newGenre: toGenre,
      affectedBranches: branches.map(b => ({ playerId: b.branchData.createdBy }))
    };
  }

  /**
   * Get branch tree visualization data
   */
  async getBranchTree(cartridgeId: string): Promise<any> {
    const branches = await this.getCartridgeBranches(cartridgeId);
    
    // Build tree structure
    const tree = {
      name: 'Main Branch',
      children: branches
        .filter(b => !b.branchData.isMainBranch)
        .map(b => ({
          name: b.branchData.name,
          karma: b.karmaState,
          commits: b.playerChoices.length,
          player: b.branchData.createdBy
        }))
    };

    return tree;
  }

  /**
   * Transform database data to GameStateBranch
   */
  private transformBranchData(data: any): GameStateBranch {
    return {
      id: data.id,
      genre: data.genre,
      cartridgeId: data.cartridge_id,
      cartridgeName: data.branch_data?.cartridgeName || 'Unknown',
      instanceId: data.instance_id,
      branchData: {
        name: data.branch_data?.name || `branch-${data.id.substring(0, 8)}`,
        head: data.branch_data?.head || 'initial',
        createdBy: data.branch_data?.createdBy || 'unknown',
        isMainBranch: data.branch_data?.isMainBranch || false,
        mergedFrom: data.branch_data?.mergedFrom || []
      },
      playerChoices: data.player_choices || [],
      karmaState: data.karma_state,
      createdAt: new Date(data.created_at),
      updatedAt: new Date(data.updated_at || data.created_at),
      mergeableWith: []
    };
  }
}

export const gitGameStateService = new GitGameStateService();
